"use client";

import Image from "next/image";
import { useParams } from "next/navigation";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
// import { useBooking } from "@/context/BookingContext";

import customImg from "@/public/images/services/custom.png";
import aiImg from "@/public/images/services/ai.png";
import webImg from "@/public/images/services/web.png";
import cmsImg from "@/public/images/services/cms.png";
import ecomImg from "@/public/images/services/ecom.png";
import consultImg from "@/public/images/services/consult.png";

const ServiceDetail = () => {
    const params = useParams();
    const serviceId = params.slug as string;
   
    const customEasing: [number, number, number, number] = [
        0.22,
        1,
        0.36,
        1,
      ];

  const servicesData = {
    "custom-software": {
      name: "Custom Software Development",
      headline: "Purpose-Built Systems for Complex Business Needs",
      description: "We design and build custom software solutions that align precisely with your workflows, scale with your growth, and integrate seamlessly with your existing systems.",
      bestSuited: "Teams with unique workflows that off-the-shelf tools can't address",
      primaryFocus: "Scalability & Business Logic",
      deliveryType: "Full Product Build",
      image: customImg,
      whatWeBuild: [
        "Workflow automation systems that eliminate manual processes",
        "Data management platforms with custom business logic",
        "Integration layers connecting multiple systems and APIs",
        "Scalable backends designed for high availability",
        "Admin interfaces and operational dashboards"
      ],
      useCases: [
        "Legacy system modernization without disrupting operations",
        "Building custom tools when SaaS solutions don't fit",
        "Automating complex multi-step business processes",
        "Creating unified platforms from disconnected systems"
      ],
      validation: "When standard solutions don't fit your business model, custom software becomes a competitive advantage—not just a tool.",
      approach: "We start by understanding your business deeply. Before writing code, we map workflows, identify bottlenecks, and design architecture that anticipates growth. Every system we build is documented, tested, and designed for maintainability.",
      accentColor: "#7A6FF0"
    },
    "ai-solutions": {
      name: "AI & Machine Learning Solutions",
      headline: "Production-Ready AI That Solves Real Problems",
      description: "We build AI systems that work in production—document processing, intelligent automation, and conversational interfaces that integrate into your existing workflows.",
      bestSuited: "Teams looking to augment operations with intelligent automation",
      primaryFocus: "Practical AI Implementation",
      deliveryType: "AI Integration & Products",
      image: aiImg,
      whatWeBuild: [
        "Document processing and intelligent data extraction",
        "RAG pipelines for knowledge bases and Q&A systems",
        "Conversational AI and chatbot interfaces",
        "Predictive analytics and decision support tools",
        "Voice-enabled applications and speech interfaces"
      ],
      useCases: [
        "Automating document review and data entry",
        "Building knowledge bases from unstructured content",
        "Adding conversational interfaces to existing products",
        "Creating recommendation and prediction systems"
      ],
      validation: "AI should augment human capabilities, not replace critical thinking. We build systems that enhance decision-making while keeping humans in control.",
      approach: "We focus on practical AI that solves specific problems. No experiments—every AI system we build is designed to work reliably in production with real data and real users.",
      accentColor: "#EC4899"
    },
    "web-mobile": {
      name: "Web & Mobile Applications",
      headline: "Responsive Applications That Work Everywhere",
      description: "We build web and mobile applications that deliver consistent, performant experiences across all devices—from complex SPAs to cross-platform mobile apps.",
      bestSuited: "Product teams building user-facing applications",
      primaryFocus: "Performance & User Experience",
      deliveryType: "Web & Mobile Apps",
      image: webImg,
      whatWeBuild: [
        "React and Next.js web applications",
        "React Native cross-platform mobile apps",
        "Progressive web apps with offline support",
        "Real-time collaborative features",
        "Complex UI with smooth interactions"
      ],
      useCases: [
        "Building MVPs that can scale to full products",
        "Creating mobile apps from existing web platforms",
        "Adding real-time features to existing applications",
        "Improving performance of slow or outdated frontends"
      ],
      validation: "Users expect fast, intuitive experiences. We build applications that feel native, load quickly, and work reliably across devices.",
      approach: "We prioritize performance and maintainability. Every application is built with clean architecture, comprehensive testing, and optimized for real-world usage patterns.",
      accentColor: "#38BDF8"
    },
    "dashboards-cms": {
      name: "Dashboards & Internal Tools",
      headline: "Operational Systems That Improve Efficiency",
      description: "We build admin panels, dashboards, and internal tools that help teams work more efficiently—with the right data, at the right time, with the right controls.",
      bestSuited: "Teams needing better visibility and control over operations",
      primaryFocus: "Data Visibility & Workflows",
      deliveryType: "Internal Platforms",
      image: cmsImg,
      whatWeBuild: [
        "Real-time analytics dashboards",
        "Custom CMS and content management systems",
        "Admin panels with role-based access control",
        "Reporting and export automation",
        "Operational workflow tools"
      ],
      useCases: [
        "Replacing spreadsheets with proper data management",
        "Building custom admin tools for unique workflows",
        "Creating dashboards for business intelligence",
        "Automating reporting and data export"
      ],
      validation: "Internal tools should feel as polished as customer-facing products. Better tools mean better operations.",
      approach: "We design internal tools with the same care as external products. Clean interfaces, fast performance, and thoughtful workflows that match how your team actually works.",
      accentColor: "#10B981"
    },
    "ecommerce-pos": {
      name: "E-commerce & POS Systems",
      headline: "Unified Commerce Platforms for Modern Retail",
      description: "We build integrated commerce solutions that connect online stores, point-of-sale systems, and inventory management—eliminating silos and manual reconciliation.",
      bestSuited: "Retail and commerce businesses with multi-channel operations",
      primaryFocus: "Channel Unification & Inventory",
      deliveryType: "Commerce Platforms",
      image: ecomImg,
      whatWeBuild: [
        "Custom e-commerce platforms and storefronts",
        "Point-of-sale systems with real-time sync",
        "Inventory management across channels",
        "Order management and fulfillment automation",
        "Payment integration and checkout optimization"
      ],
      useCases: [
        "Unifying online and offline sales channels",
        "Replacing disconnected inventory systems",
        "Building custom checkout and payment flows",
        "Automating order routing and fulfillment"
      ],
      validation: "Modern commerce requires real-time visibility across all channels. We build systems that eliminate manual work and reduce errors.",
      approach: "We design commerce systems that treat inventory and orders as a single source of truth. No more discrepancies, no more manual reconciliation.",
      accentColor: "#F59E0B"
    },
    "consulting": {
      name: "Technical Consultation & Strategy",
      headline: "Strategic Guidance for Technical Decisions",
      description: "We provide technical consultation for teams planning builds, navigating complex decisions, or needing expert guidance on architecture and technology choices.",
      bestSuited: "Teams at decision points or planning major technical initiatives",
      primaryFocus: "Strategic Clarity",
      deliveryType: "Advisory & Planning",
      image: consultImg,
      whatWeBuild: [
        "Architecture reviews and recommendations",
        "Technology selection and roadmapping",
        "Technical due diligence for acquisitions",
        "Team structure and process optimization",
        "Knowledge transfer and documentation"
      ],
      useCases: [
        "Planning a major platform rebuild or migration",
        "Evaluating technology choices for new products",
        "Auditing existing systems for scalability issues",
        "Getting second opinions on architectural decisions"
      ],
      validation: "Good technical decisions save months of work and significant cost. We help you make the right choices before committing resources.",
      approach: "We provide honest, practical guidance based on real-world experience. No vendor bias, no theoretical frameworks—just clear recommendations you can act on.",
      accentColor: "#7A6FF0"
    },
    "saas-platforms": {
      name: "SaaS Platform Development",
      headline: "Scalable SaaS Products Built for Growth",
      description: "We design and develop multi-tenant SaaS platforms that help businesses launch, scale, and manage software products with confidence.",
      bestSuited: "Startups and businesses building subscription-based software products",
      primaryFocus: "Scalability & Product Growth",
      deliveryType: "End-to-End SaaS Platforms",
      image: consultImg,
      whatWeBuild: [
        "Multi-tenant SaaS applications",
        "Subscription and billing systems",
        "Admin panels and user management",
        "Customer portals and dashboards",
        "API-first backend architectures"
      ],
      useCases: [
        "Launching a new SaaS product from scratch",
        "Scaling an existing SaaS platform",
        "Building customer self-service portals",
        "Creating subscription-based business models"
      ],
      validation: "Successful SaaS products require more than features—they need strong architecture, reliability, and a seamless user experience that supports growth.",
      approach: "We build SaaS products with scalability, security, and maintainability at the core, ensuring your platform can evolve alongside your business and customers.",
      accentColor: "#7A6FF0"
    },
    
    "ai-product-development": {
      name: "AI Product Development",
      headline: "Transforming Ideas into AI-Powered Products",
      description: "We help businesses build AI-driven applications that automate workflows, unlock insights, and create entirely new user experiences.",
      bestSuited: "Companies looking to launch AI-powered products or integrate AI into existing platforms",
      primaryFocus: "AI Innovation & Automation",
      deliveryType: "AI Products & Intelligent Systems",
      image: consultImg,
      whatWeBuild: [
        "AI copilots and assistants",
        "LLM-powered applications",
        "Document intelligence systems",
        "AI workflow automation tools",
        "Custom AI integrations and APIs"
      ],
      useCases: [
        "Building AI-first SaaS products",
        "Automating manual business operations",
        "Creating intelligent customer experiences",
        "Developing knowledge and recommendation systems"
      ],
      validation: "The value of AI comes from solving real business problems. We focus on practical implementations that deliver measurable outcomes.",
      approach: "From strategy to deployment, we combine product thinking, modern AI technologies, and scalable engineering to build AI solutions ready for production.",
      accentColor: "#EC4899"
    },
    
    "product-design": {
      name: "Product Design & UX",
      headline: "Designing Experiences People Love to Use",
      description: "We create user-centered digital experiences that balance usability, business goals, and modern visual design across web and mobile products.",
      bestSuited: "Teams building new products or improving existing user experiences",
      primaryFocus: "User Experience & Product Strategy",
      deliveryType: "Research, UX & UI Design",
      image: consultImg,
      whatWeBuild: [
        "Product discovery workshops",
        "User experience design",
        "User interface design systems",
        "Interactive prototypes and wireframes",
        "Design systems and component libraries"
      ],
      useCases: [
        "Designing MVPs for startups",
        "Redesigning existing products",
        "Improving conversion and usability",
        "Creating scalable design systems"
      ],
      validation: "Great products are not only functional—they are intuitive, memorable, and aligned with user needs and business objectives.",
      approach: "We combine research, strategy, and visual design to create experiences that feel simple, engaging, and effective from the first interaction.",
      accentColor: "#F59E0B"
    }
  };

//   const service = servicesData[serviceId];
const service =
servicesData[serviceId as keyof typeof servicesData];
  if (!service) {
    return (
      <div className="min-h-screen py-20 bg-[#FAFAFA]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-[32px] text-[#0F172A] mb-4" style={{ fontWeight: 500 }}>Service Not Found</h1>
          <p className="text-[16px] text-[#64748B]">The service you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAFA]">
      {/* Service Hero with Gradient Wash */}
      <section
        className="pt-24 pb-16 relative overflow-hidden"
        style={{
          background: `
            radial-gradient(900px 400px at 20% 0%, rgba(122,111,240,0.12), transparent 60%),
            radial-gradient(700px 400px at 80% 20%, rgba(242,166,90,0.12), transparent 60%),
            #FFFFFF
          `
        }}
      >
        {/* Floating Blob - Slow Drift Animation */}
        <motion.div
          className="absolute -right-32 top-1/4 w-96 h-96 rounded-full opacity-40 blur-3xl pointer-events-none"
          style={{ background: `${service.accentColor}30` }}
          animate={{
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:flex lg:gap-12 lg:items-start">
            {/* Left - Editorial Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: customEasing }}
              className="lg:w-[58%] mb-10 lg:mb-0"
            >
              {/* Service Label */}
              <p
                className="text-[11px] uppercase tracking-[0.12em] mb-4"
                style={{ fontWeight: 500, color: service.accentColor }}
              >
                {service.name}
              </p>

              {/* Headline */}
              <h1 className="text-[32px] sm:text-[40px] md:text-[44px] text-[#0F172A] mb-6" style={{ fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                {service.headline}
              </h1>

              {/* Description */}
              <p className="text-[17px] text-[#475569]" style={{ fontWeight: 400, lineHeight: 1.65 }}>
                {service.description}
              </p>
            </motion.div>

            {/* Right - Service Context Panel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1, ease: customEasing }}
              className="lg:w-[38%]"
            >
              <div
                className="p-6 rounded-xl backdrop-blur-sm"
                style={{
                  backgroundColor: "rgba(255,255,255,0.8)",
                  border: "1px solid #E5E7EB"
                }}
              >
                <div className="space-y-4">
                  {[
                    { label: "Best Suited For", value: service.bestSuited },
                    { label: "Primary Focus", value: service.primaryFocus },
                    { label: "Delivery Type", value: service.deliveryType }
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      className="pb-4"
                      style={{ borderBottom: idx < 2 ? "1px solid #E5E7EB" : "none" }}
                    >
                      <p className="text-[11px] uppercase tracking-wide text-[#94A3B8] mb-1" style={{ fontWeight: 500 }}>
                        {item.label}
                      </p>
                      <p className="text-[14px] text-[#334155]" style={{ fontWeight: 500 }}>
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2, ease: customEasing }}
            className="mt-12"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              {/* Gradient overlay */}
              <div
                className="absolute inset-0 opacity-20 pointer-events-none z-10"
                style={{ background: `linear-gradient(135deg, ${service.accentColor}40, transparent 60%)` }}
              />
              <div className="relative w-full h-96">
  <Image
    src={service.image}
    alt={service.name}
    fill
    className="object-cover"
  />
</div>
              {/* Bottom gradient fade */}
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white/80 to-transparent pointer-events-none z-10" />
            </div>
          </motion.div>
        </div>

        {/* Gradient Divider */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background: "linear-gradient(to right, transparent, rgba(122,111,240,0.3), transparent)"
          }}
        />
      </section>

      {/* What We Build - Pill Layout */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-[24px] sm:text-[28px] text-[#0F172A] mb-3" style={{ fontWeight: 500, lineHeight: 1.2 }}>
              What We Build
            </h2>
          </motion.div>

          {/* Pill-based Layout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex flex-wrap gap-3"
          >
            {service.whatWeBuild.map((item, idx) => (
              <motion.span
                key={idx}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.28, delay: idx * 0.05 }}
                className="px-4 py-2.5 rounded-full text-[14px] text-[#334155]"
                style={{
                  background: `${service.accentColor}10`,
                  fontWeight: 500
                }}
              >
                {item}
              </motion.span>
            ))}
          </motion.div>
        </div>

        {/* Gradient Divider */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background: "linear-gradient(to right, transparent, rgba(122,111,240,0.3), transparent)"
          }}
        />
      </section>

      {/* When This Is a Fit / Use Cases */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:gap-16">
            {/* Left - Use Cases */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3 }}
              className="lg:w-[55%] mb-10 lg:mb-0"
            >
              <h2 className="text-[24px] sm:text-[28px] text-[#0F172A] mb-6" style={{ fontWeight: 500, lineHeight: 1.2 }}>
                When This Is a Fit
              </h2>

              <div className="space-y-3">
                {service.useCases.map((useCase, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.28, delay: idx * 0.07, ease: customEasing }}
                    className="flex items-start gap-3"
                  >
                    <Check
                      className="w-4 h-4 mt-1 flex-shrink-0"
                      style={{ color: service.accentColor }}
                    />
                    <p className="text-[15px] text-[#475569]" style={{ fontWeight: 400, lineHeight: 1.6 }}>
                      {useCase}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right - Validation Copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="lg:w-[40%]"
            >
              <div
                className="p-6 rounded-xl"
                style={{
                  backgroundColor: `${service.accentColor}08`,
                  borderLeft: `3px solid ${service.accentColor}`
                }}
              >
                <p className="text-[15px] text-[#334155]" style={{ fontWeight: 500, lineHeight: 1.65 }}>
                  {service.validation}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
            className="max-w-3xl"
          >
            <h2 className="text-[24px] sm:text-[28px] text-[#0F172A] mb-6" style={{ fontWeight: 500, lineHeight: 1.2 }}>
              Our Approach
            </h2>

            <div
              className="relative pl-6"
              style={{ borderLeft: `2px solid ${service.accentColor}40` }}
            >
              <p className="text-[16px] text-[#475569]" style={{ fontWeight: 400, lineHeight: 1.7 }}>
                {service.approach}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Gradient Divider */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background: "linear-gradient(to right, transparent, rgba(122,111,240,0.3), transparent)"
          }}
        />
      </section>

      {/* Global CTA */}
      <section
        className="py-12 sm:py-16 lg:py-20 relative overflow-hidden"
        style={{
          background: `
            radial-gradient(500px 200px at 30% 0%, rgba(242,166,90,0.35), transparent 60%),
            radial-gradient(500px 200px at 70% 100%, rgba(122,111,240,0.35), transparent 60%),
            #0B0B0F
          `
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:gap-12 lg:items-start">
            {/* Left Column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.32 }}
              className="lg:w-[58%] mb-8 lg:mb-0"
            >
              <h2 className="text-2xl sm:text-[28px] md:text-[34px] lg:text-[40px] text-white mb-4" style={{ fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
                Ready to Build a System That Scales?
              </h2>
              <p className="text-base sm:text-[17px] text-white/70 mb-4 sm:mb-6" style={{ fontWeight: 400, lineHeight: 1.6 }}>
                Whether you're starting from scratch or improving an existing product, we can help you build software that supports your growth.
              </p>
              <p className="text-sm sm:text-[15px] text-white/60" style={{ fontWeight: 400, lineHeight: 1.7 }}>
                Every engagement starts with a conversation. We'll discuss your goals, constraints, and what success looks like for your project.
              </p>
            </motion.div>

            {/* Right Column - CTA Panel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.32, delay: 0.1 }}
              className="lg:w-[42%]"
            >
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 sm:p-8">
                <p className="text-[11px] sm:text-[12px] uppercase tracking-wide text-[#7A6FF0] mb-4 sm:mb-6 text-center" style={{ fontWeight: 500 }}>
                  Next Step
                </p>
                <p className="text-sm sm:text-[14px] text-white/70 mb-4 sm:mb-6 text-center" style={{ fontWeight: 400, lineHeight: 1.6 }}>
                  Schedule a free consultation to discuss your project requirements.
                </p>

                <motion.button
                //   onClick={openBookingModal}
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.16 }}
                  className="w-full inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm sm:text-[15px] font-semibold text-[#0B0B0F] transition-all duration-200 hover:bg-white/90 hover:shadow-lg hover:shadow-white/10 mb-4"
                >
                  Book a Free Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </motion.button>

                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  <button
                    // onClick={openContactModal}
                    className="text-sm sm:text-[14px] text-white/70 hover:text-white transition-colors"
                    style={{ fontWeight: 500 }}
                  >
                    Contact Us
                  </button>
                  <span className="text-white/30">·</span>
                  <a
                    href="mailto:hello@zysoftec.com"
                    className="text-sm sm:text-[14px] text-white/70 hover:text-white transition-colors"
                    style={{ fontWeight: 500 }}
                  >
                    Email Us
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
